import React from "react";
import { Badge, NumberCircle } from "./UI";

function IssueCard({ badge, badgeVariant, title, causesLabel, causes, fixLabel, fixes, emailSubject, emailInclude, responseTime }) {
  return (
    <div className="sz-issue-card">
      <div>
        <Badge variant={badgeVariant}>{badge}</Badge>
        <span className="sz-issue-title">{title}</span>
      </div>

      <div className="sz-issue-subhead">{causesLabel}</div>
      <ul className="sz-bullet-list mb-0">
        {causes.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>

      <div className="sz-issue-subhead">{fixLabel}</div>
      <div className="sz-fix-box">
        <ol>
          {fixes.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ol>
      </div>

      <div className="sz-fix-box mt-3">
        <p className="fw-bold mb-1">If still not working, send to support</p>
        <div className="sz-support-subject">{emailSubject}</div>
        <p className="fw-semibold mb-1 mt-2">Include:</p>
        <ul className="mb-2">
          {emailInclude.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
        {responseTime && (
          <p className="mb-0 fst-italic">{responseTime}</p>
        )}
      </div>
    </div>
  );
}

export default function Troubleshooting() {
  return (
    <div className="sz-page-section sz-page-section-alt">
      <div className="container">
        <h2 className="sz-plain-title mb-1">Troubleshooting:</h2>
        <p className="fw-semibold fs-5 mb-3">
          What Goes Wrong and How to Fix (Step by Step)
        </p>
        <p>
          When something isn't working as it should on Selectezi, follow
          these step by step tests first.
        </p>
        <p className="mb-4">
          If it still doesn't work, go to the "Contact Support" section below
          and include the requested details so our team can resolve your
          issue promptly.
        </p>

        <IssueCard
          badge="Issue"
          badgeVariant="danger"
          title="Can't Sign Up"
          causesLabel="Why It Often Occurs"
          causes={[
            "Required details were not filled out.",
            "Incorrect email format or email already taken.",
            "Password does not meet security guidelines.",
            "Incorrect mobile number or country code.",
            "Browser or network issue preventing form submission.",
            "Captcha or anti-bot verification failed.",
          ]}
          fixLabel="Step by Step Fix"
          fixes={[
            "Fill in all mandatory fields, look for red highlights or inline error messages.",
            "Email format: use name@domain.com.",
            'If "email already registered," try Forgot Password for that email.',
            "Password: minimum 8 characters, include one uppercase letter and a number. Avoid weak passwords (e.g., password123).",
            "Phone number: include country code and no extra spaces or symbols (e.g., +92XXXXXXXXXX).",
            "Captcha: complete correctly; refresh and retry if expired.",
            "Switch browser or device: try Chrome or Firefox desktop, or mobile app if available.",
            "Clear cache and cookies or use incognito/private window.",
            "Disable browser extensions (adblockers, privacy blockers).",
            "Try another network (switch Wi-Fi to mobile data).",
            "Check file format/size if image upload is required (JPG/PNG under 2MB).",
          ]}
          emailSubject="Subject: Signup Issue - [Your Name] - [Email Address]"
          emailInclude={[
            "Exact error message shown.",
            "Steps attempted (numbered list).",
            "Screenshot of error.",
            "Email used, mobile number (+country code), browser and version, device, date/time of attempt.",
          ]}
          responseTime="Expected response: 24 to 48 hours."
        />

        <IssueCard
          badge="Issue"
          badgeVariant="warning"
          title="Can't Log In"
          causesLabel="Common Causes"
          causes={[
            "Email not verified.",
            "Incorrect password or spaces included.",
            "Temporary account lock after failed attempts.",
            "Corrupted browser cookies/session.",
            "Network or server issue.",
          ]}
          fixLabel="Step by Step Fix"
          fixes={[
            "Verify email: check Inbox, Spam, or Promotions. If missing, click Resend Verification.",
            "Forgot Password: request reset link and follow instructions.",
            "Re-type password manually (avoid copy paste).",
            "Clear cache and cookies or use incognito/private mode.",
            "Try another browser or device.",
            "Check for account lock: wait 15 to 30 minutes or contact support.",
            "Ensure stable internet connection.",
            "Update app/browser to the latest version.",
          ]}
          emailSubject="Subject: Login Issue - [Your Name] - [Email Address]"
          emailInclude={[
            "Exact error message.",
            "Whether email is verified.",
            "Actions attempted.",
            "Device and OS (e.g., Android 12 / iOS 17 / Windows 10).",
            "Browser/app version.",
            "Screenshots.",
            "If account locked: mention time of first lockout.",
          ]}
        />

        <IssueCard
          badge="Issue"
          badgeVariant="info"
          title="Can't Post or Upload (Timeline, Product Image, Ad, etc.)"
          causesLabel="Common Causes"
          causes={[
            "File too large or unsupported format.",
            "Account lacks posting privileges.",
            "Temporary server or network issue.",
            "Cache or storage issue.",
            "Content violates community rules.",
          ]}
          fixLabel="Step by Step Fix"
          fixes={[
            "Check file type and size: only JPG/PNG, under 2MB.",
            "Resize or compress using Paint, Preview, or mobile apps.",
            "Rename file (e.g., product1.jpg, avoid special symbols).",
            "Test smaller image to confirm upload works.",
            "Check permissions, unverified accounts may not post in all categories.",
            "Clear cache and cookies or use incognito mode.",
            "Try another network (mobile data/Wi-Fi).",
            "Update browser or app to latest version.",
            "If rejected, check moderation message for reason and adjust post.",
            "Try uploading from another device (mobile to desktop).",
          ]}
          emailSubject="Subject: Upload/Post Issue - [Your Name] - [Email/Business Name]"
          emailInclude={[
            "File/image you tried to upload (if possible).",
            "Screenshot/video of failed upload.",
            "Exact post text.",
            "Browser/app version, device, internet type, time/date, account ID.",
          ]}
        />

        <h4 className="fw-bold mt-5 mb-3" style={{ color: "var(--sz-text-dark)" }}>
          Other Issues and Quick Workarounds
        </h4>
        <div className="row g-3">
          <div className="col-md-6">
            <NumberCircle number="•">
              <strong>OTP / SMS not received</strong>
              <ul className="mb-0 mt-1">
                <li>Check mobile signal.</li>
                <li>Ensure correct number with country code.</li>
                <li>Wait 1 to 2 minutes and retry.</li>
                <li>If still not received, try another number or contact support with timestamp.</li>
              </ul>
            </NumberCircle>
          </div>
          <div className="col-md-6">
            <NumberCircle number="•">
              <strong>Verification Email blocked</strong>
              <ul className="mb-0 mt-1">
                <li>Check Spam/Junk/Promotions folders.</li>
                <li>For work emails, ask IT to whitelist @selectezi.com or use personal email (e.g., Gmail).</li>
              </ul>
            </NumberCircle>
          </div>
          <div className="col-md-6">
            <NumberCircle number="•">
              <strong>App Crashes or Freezes</strong>
              <ul className="mb-0 mt-1">
                <li>Force close app and reopen.</li>
                <li>Restart device.</li>
                <li>Free up storage.</li>
                <li>Reinstall app (you'll need to log in again).</li>
              </ul>
            </NumberCircle>
          </div>
          <div className="col-md-6">
            <NumberCircle number="•">
              <strong>Account Suspended or Restricted</strong>
              <ul className="mb-0 mt-1">
                <li>Read message for reason (policy, pending verification, etc.).</li>
                <li>Follow given steps or contact support for clarification.</li>
              </ul>
            </NumberCircle>
          </div>
        </div>
      </div>
    </div>
  );
}
